import Mustache from 'mustache';

interface BasketOptions {
  language: string;
  baseUrl: string;
}

interface Item {
  id: string;
  type: string;
  name: string;
  url: string;
}

interface Job {
  /** Job id */
  job: string;
}

interface JobStatus {
  status: 'new' | 'running' | 'failed' | 'done';
  data: any;
}

/**
 * API client for the basket backend.
 *
 * Manages the server-side shopping basket and PDF generation jobs.
 * Each instance is tied to a single language (defaults to the page language).
 * All requests are sent to `/stage-ajax/basket` with a `cm_lang` query parameter.
 */
class Basket {
  settings: BasketOptions;
  isGeneratingPdf: boolean = false;
  currentJobId: string | null = null;
  #pollTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * @param {Partial<BasketOptions>} options - Optional overrides for language and base URL.
   */
  constructor(options: Partial<BasketOptions> = {}) {
    const defaultOptions: BasketOptions = {
      language: document.documentElement.lang || 'fi',
      baseUrl: '/stage-ajax/basket',
    };

    this.settings = { ...defaultOptions, ...options };
  }

  /**
   * Fetches the current basket items from the server.
   *
   * @returns {Promise<Item[]>} The list of items currently in the basket.
   */
  getItems(): Promise<Item[]> {
    return this.#request('items', 'GET');
  }

  /**
   * Adds an item to the basket.
   *
   * @param {{ type: string, id: string }} item - The item type and ID to add.
   * @returns {Promise<Item[]>} The updated list of basket items.
   */
  addItem(item: Pick<Item, 'type' | 'id'>): Promise<Item[]> {
    return this.#request('items', 'POST', item);
  }

  /**
   * Removes a single item from the basket by its ID.
   *
   * @param {string} itemId - The ID of the item to remove.
   * @returns {Promise<Item[]>} The updated list of basket items.
   */
  removeItem(itemId: Item['id']): Promise<Item[]> {
    return this.#request(`items/${itemId}`, 'DELETE');
  }

  /**
   * Removes all items from the basket.
   *
   * @returns {Promise<Item[]>} An empty array confirming the basket was cleared.
   */
  empty(): Promise<Item[]> {
    return this.#request('items', 'DELETE');
  }

  /**
   * Initiates PDF generation for the current basket contents.
   *
   * Throws if generation is already in progress. Polls the job status and calls
   * `listener` on every status update until the job reaches a terminal state.
   *
   * @param {function(JobStatus): void} listener - Callback invoked on each poll response.
   * @returns {Promise<void>} Resolves once the job is registered and polling has started.
   */
  requestPdf(listener: (status: JobStatus) => void): Promise<void> {
    if (this.isGeneratingPdf) {
      throw new Error('PDF generation is already in progress');
    }

    this.isGeneratingPdf = true;

    return this.#request('pdf', 'POST')
      .then((response: Job) => {
        if (!response.job) {
          throw new Error('No job ID returned from PDF generation request');
        }

        this.currentJobId = response.job;
        this.#poll(listener);
      })
      .catch((error) => {
        console.error('[Basket] Error requesting PDF generation', error);
        this.isGeneratingPdf = false;
        throw error;
      });
  }

  /**
   * Polls the PDF job status at a fixed interval and calls `listener` on each update.
   *
   * Continues polling while status is `'new'` or `'running'`. Stops and resets
   * internal state on `'done'` or `'failed'`.
   *
   * @param {function(JobStatus): void} listener - Callback invoked with the latest job status.
   */
  #poll(listener: (status: JobStatus) => void): void {
    if (!this.currentJobId) {
      console.error('[Basket] No current job ID to poll');
      return;
    }

    const interval = 2000;

    this.#request(`pdf/${this.currentJobId}`, 'GET')
      .then((response: JobStatus) => {
        listener(response);

        switch (response.status) {
        case 'done':
          this.isGeneratingPdf = false;
          this.currentJobId = null;
          break;
        case 'failed':
          this.isGeneratingPdf = false;
          this.currentJobId = null;
          break;
        case 'running':
          this.#pollTimer = setTimeout(this.#poll.bind(this), interval, listener);
          break;
        default:
          this.#pollTimer = setTimeout(this.#poll.bind(this), interval, listener);
        }
      });
  }

  /**
   * Sends or fetches requests from the server.
   *
   * @param {string} endpoint - The API endpoint path (appended after the base URL).
   * @param {string} method - The HTTP method to use (default is `'GET'`).
   * @param {object} data - Additional data; sent as query params for GET, JSON body for POST/PUT.
   * @returns {Promise<any>} Resolves to the parsed JSON response from the server.
   */
  #request(endpoint: string, method = 'GET', data = {}): Promise<any> {
    const queryParams = method === 'GET' ? {
      ...data,
      cm_lang: this.settings.language,
    } : {
      cm_lang: this.settings.language,
    };

    const queryString = new URLSearchParams(queryParams).toString();
    const url = `${this.settings.baseUrl}/${endpoint}${queryString ? '?' + queryString : ''}`;

    return fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: ['POST', 'PUT'].includes(method) ? JSON.stringify(data) : undefined,
    })
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        return response.text().then((text) => { throw new Error(text); });
      })
      .catch((error) => {
        console.error('[basket-api] Error', error);
        throw error;
      });
  }

  /**
   * Cancels an in-progress PDF generation job and clears the poll timer.
   *
   * This is a client-side cancel only — it does not notify the server.
   */
  cancelPdfGeneration() {
    this.isGeneratingPdf = false;
    this.currentJobId = null;
    if (this.#pollTimer) {
      clearTimeout(this.#pollTimer);
      this.#pollTimer = null;
    }
  }
}

/**
 * UI controller for the basket widget.
 *
 * Binds to existing DOM markup via class/id selectors and delegates all
 * server communication to a `Basket` instance. Handles:
 * - Add / remove / empty basket actions
 * - Basket panel toggle with focus trap and focus restoration
 * - PDF generation progress, success, and error states
 * - Mustache-rendered item list
 *
 * Expects the following markup (default state — empty basket):
 *
 * ```html
 * <button class="js-basket-add" data-basket-type="article" data-basket-id="123">Add to basket</button>
 * <button class="js-basket-remove" data-basket-id="123" hidden>Remove from basket</button>
 * <button class="js-basket-empty" disabled>Empty basket</button>
 * <button class="js-basket-generate" disabled>Generate PDF</button>
 *
 * <button class="js-basket-trigger" aria-controls="basket" aria-expanded="false">Toggle basket</button>
 * <button class="js-basket-close" aria-controls="basket">Close basket</button>
 *
 * <ul class="js-basket-list" hidden></ul>
 * <p class="js-empty-basket-message">Your basket is empty.</p>
 * ```
 */
class BasketUI {
  #basket: Basket;
  items: Item[] = [];
  count: number = 0;
  #initialized: boolean = false;
  #basketItemTemplate: string | null = null;
  #basketListContainer: HTMLElement | null = null;
  #basketListTemplate: HTMLElement | null = null;
  #basketProgressTemplate: HTMLElement | null = null;
  #basketSuccessTemplate: HTMLElement | null = null;
  #basketErrorTemplate: HTMLElement | null = null;
  #basketDialogTemplate: string | null = null;
  #focusTrapHandler: ((event: KeyboardEvent) => void) | null = null;
  #lastFocusedElement: HTMLElement | null = null;
  #downloadLink: HTMLAnchorElement | null = null;
  #currentVisibleContainer: 'list' | 'progress' | 'success' | 'error' | null = null;

  /**
   * @param {Basket} basket - The server API client instance to delegate to.
   */
  constructor(basket: Basket) {
    this.#basket = basket;
  }

  /**
   * Initialises the basket UI: loads DOM templates, fetches current basket items,
   * and registers event listeners.
   *
   * Safe to call multiple times — subsequent calls are no-ops.
   */
  init() {
    if (this.#initialized) {
      return;
    }
    this.#initialized = true;

    this.#loadBasketItemTemplate();
    this.#loadBasketProgressTemplate();
    this.#loadBasketListTemplate();
    this.#loadBasketSuccessTemplate();
    this.#loadBasketErrorTemplate();
    this.#loadBasketListContainer();
    this.#loadDownloadLink();

    // Force the closed-panel accessibility state at boot. handleBasketTrigger/
    // handleBasketClose only ever set aria-hidden/inert/sr-only in response to a
    // click — they never run on page load. If the server-rendered markup ships
    // the (visually closed) panel without these attributes already set, screen
    // readers can read its contents until the user has opened and closed it once.
    this.#initBasketPanelState();

    // Drive panels into a known state: hides success/error that may lack the
    // 'hidden' attribute in the HTML template and reveals the list panel.
    this.toggleContainerVisibility('list');

    this.#basket.getItems()
      .then((items) => {
        this.items = items;
        this.updateUI();
      })
      .catch((error) => {
        console.error('[BasketUI] Error fetching initial items', error);
      });

    this.initEventListeners();
  }

  /**
   * Ensures every basket panel targeted by a `.js-basket-trigger` starts hidden
   * from assistive technology, independent of whatever attributes the
   * server-rendered markup happens to ship with.
   *
   * The basket always starts closed on page load, so this applies the same
   * `sr-only` / `aria-hidden` / `inert` combination that `handleBasketClose`
   * uses, plus `aria-expanded="false"` on every trigger that controls it.
   */
  #initBasketPanelState() {
    const triggers = Array.from(document.querySelectorAll('.js-basket-trigger')) as HTMLElement[];
    const panelIds = new Set(
      triggers
        .map((trigger) => trigger.getAttribute('aria-controls'))
        .filter((id): id is string => !!id)
    );

    panelIds.forEach((id) => {
      const basketElement = document.getElementById(id);
      if (!basketElement) return;

      basketElement.classList.add('sr-only');
      basketElement.setAttribute('aria-hidden', 'true');
      basketElement.setAttribute('inert', '');
    });

    triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
  }

  #loadBasketItemTemplate() {
    const templateElement = document.getElementById('basket-item-template');

    if (!templateElement) {
      console.error('[BasketUI] Template element with id "basket-item-template" not found');
      return;
    }

    this.#basketItemTemplate = templateElement.innerHTML;
  }

  #loadBasketListContainer() {
    const containerElement = document.querySelector('.basket__list-container') as HTMLElement;

    if (!containerElement) {
      console.error('[BasketUI] Container element with class "basket__list-container" not found');
      return;
    }

    this.#basketListContainer = containerElement;
  }

  #loadBasketProgressTemplate() {
    const templateElement = document.querySelector('.progress-holder') as HTMLElement;

    if (!templateElement) {
      console.error('[BasketUI] Template element with class "progress-holder" not found');
      return;
    }

    this.#basketProgressTemplate = templateElement;
  }

  #loadBasketListTemplate() {
    const templateElements = document.querySelectorAll('.js-basket-list') as unknown as HTMLElement[];

    if (templateElements.length === 0) {
      console.error('[BasketUI] No template elements with class "js-basket-list" found');
      return;
    }

    this.#basketListTemplate = templateElements[0];
  }

  #loadBasketSuccessTemplate() {
    const templateElement = document.querySelector('.show-on-success') as HTMLElement;

    if (!templateElement) {
      console.error('[BasketUI] Template element with class "show-on-success" not found');
      return;
    }

    this.#basketSuccessTemplate = templateElement;
  }

  #loadBasketErrorTemplate() {
    const templateElement = document.querySelector('.show-on-error') as HTMLElement;

    if (!templateElement) {
      console.error('[BasketUI] Template element with class "show-on-error" not found');
      return;
    }

    this.#basketErrorTemplate = templateElement;
  }

  #loadDownloadLink() {
    const downloadElement = document.querySelector('.js-download') as HTMLAnchorElement;

    if (!downloadElement) {
      console.error('[BasketUI] Download link element with class "js-download" not found');
      return;
    }

    this.#downloadLink = downloadElement;
  }

  /**
   * Attaches a single delegated click listener on `document` that routes to the
   * appropriate handler based on the closest matching ancestor of the clicked target.
   */
  initEventListeners() {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;

      const addButton = target.closest('.js-basket-add');
      const removeButton = target.closest('.js-basket-remove');
      const emptyButton = target.closest('.js-basket-empty');
      const basketTrigger = target.closest('.js-basket-trigger');
      const basketCloseButton = target.closest('.js-basket-close');
      const generationButton = target.closest('.js-basket-generate');
      const cancelButton = target.closest('.js-basket-cancel');
      const downloadLink = target.closest('.js-download');

      if (addButton) {
        event.preventDefault();
        this.handleBasketAdd(addButton as HTMLElement);
      } else if (removeButton) {
        event.preventDefault();
        this.handleBasketRemove(removeButton as HTMLElement);
      } else if (emptyButton) {
        event.preventDefault();
        this.handleBasketEmpty();
      } else if (basketTrigger) {
        event.preventDefault();
        this.handleBasketTrigger(basketTrigger as HTMLElement);
      } else if (basketCloseButton) {
        event.preventDefault();
        this.handleBasketClose(basketCloseButton as HTMLElement);
      } else if (generationButton) {
        event.preventDefault();
        this.requestPdfGeneration();
      } else if (cancelButton) {
        event.preventDefault();
        this.handleBasketCancel(cancelButton as HTMLElement);
      } else if (downloadLink) {
        event.preventDefault();
        this.handleDownload(downloadLink as HTMLElement);
      }
    });
  }

  /**
   * Handles a click on a `.js-basket-add` button.
   *
   * Reads `data-basket-type` and `data-basket-id` from the button, calls the API,
   * updates the UI, and briefly animates the basket count badge to signal the addition.
   *
   * @param {HTMLElement} button - The clicked add button.
   */
  handleBasketAdd(button: HTMLElement) {
    const itemType = button.dataset.basketType;
    const itemId = button.dataset.basketId;

    if (!itemType || !itemId) {
      console.error('[BasketUI] Missing item type or ID');
      return;
    }

    this.#basket.addItem({ type: itemType, id: itemId } as Item)
      .then((response) => {
        this.items = response;
        this.updateUI();
        this.updateStatusMessage('added', 'success', this.count);
      })
      .catch((error) => {
        console.error('[BasketUI] Error adding item', error);
        this.updateStatusMessage('error_add', 'error', this.count);
      });

    document.querySelector('header')?.classList.add('fixed-menu-down');
    document.querySelector('.basket-count')?.classList.add('animate-bounce');
    setTimeout(() => {
      document.querySelector('.basket-count')?.classList.remove('animate-bounce');
    }, 5000);
  }

  /**
   * Handles a click on a `.js-basket-remove` button.
   *
   * Reads `data-basket-id` from the button and removes that item from the basket.
   *
   * @param {HTMLElement} button - The clicked remove button.
   */
  handleBasketRemove(button: HTMLElement) {
    const itemId = button.dataset.basketId;

    if (!itemId) {
      console.error('[BasketUI] Missing item ID for removal');
      return;
    }

    this.#basket.removeItem(itemId)
      .then((response) => {
        this.items = response;
        this.updateUI();
        this.updateStatusMessage('removed', 'success', this.count);
      })
      .catch((error) => {
        console.error('[BasketUI] Error removing item', error);
        this.updateStatusMessage('error_remove', 'error', this.count);
      });
  }

  /**
   * Handles a click on the `.js-basket-empty` button.
   *
   * Clears all items from the basket and refreshes the UI.
   */
  handleBasketEmpty() {
    this.#basket.empty()
      .then((response) => {
        this.items = response;
        this.updateUI();
      })
      .catch((error) => {
        console.error('[BasketUI] Error emptying basket', error);
      });
  }

  /**
   * Toggles the basket panel open or closed.
   *
   * When opening: removes `sr-only`, `aria-hidden`, and `inert` from the panel,
   * stores focus origin, sets up a focus trap, and moves focus to the h2 heading
   * so screen readers announce only the panel title on open.
   * When closing: restores `sr-only`, `aria-hidden`, and `inert`, removes the
   * focus trap, and returns focus to the trigger button.
   *
   * Updates `aria-expanded` on the trigger to reflect the new state.
   *
   * @param {HTMLElement} button - The `.js-basket-trigger` button that was clicked.
   */
  handleBasketTrigger(button: HTMLElement) {
    const basketElement = document.getElementById(button.getAttribute('aria-controls') || '');

    if (!basketElement) {
      console.error('[BasketUI] Basket element not found');
      return;
    }

    const isHidden = basketElement.classList.contains('sr-only');
    basketElement.classList.toggle('sr-only', !isHidden);
    basketElement.setAttribute('aria-hidden', isHidden ? 'false' : 'true');
    basketElement.toggleAttribute('inert', !isHidden);
    button.setAttribute('aria-expanded', isHidden ? 'true' : 'false');

    if (isHidden) {
      this.#lastFocusedElement = button;
      this.#setupFocusTrap(basketElement);

      setTimeout(() => {
        const heading = basketElement.querySelector('h2') as HTMLElement;
        const focusTarget = heading || basketElement;
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus();
      }, 0);
    } else {
      this.#removeFocusTrap();
      this.#lastFocusedElement?.focus();
    }
  }

  /**
   * Closes the basket panel in response to a `.js-basket-close` button click.
   *
   * Adds `sr-only`, `aria-hidden="true"`, and `inert` to the panel so it is
   * hidden from both visual users and screen readers and excluded from tab order.
   * Sets `aria-expanded="false"` on the associated trigger, removes the focus
   * trap, and restores focus to the last focused element.
   *
   * @param {HTMLElement} button - The close button that was clicked.
   */
  handleBasketClose(button: HTMLElement) {
    const basketElement = document.getElementById(button.getAttribute('aria-controls') || '');

    if (!basketElement) {
      console.error('[BasketUI] Basket element not found');
      return;
    }

    basketElement.classList.add('sr-only');
    basketElement.setAttribute('aria-hidden', 'true');
    basketElement.setAttribute('inert', '');

    const triggerButton = document.querySelector(`[aria-controls="${basketElement.id}"]`) as HTMLElement;
    if (triggerButton) {
      triggerButton.setAttribute('aria-expanded', 'false');
    }

    this.#removeFocusTrap();

    if (this.#lastFocusedElement) {
      this.#lastFocusedElement.focus();
    } else if (triggerButton) {
      triggerButton.focus();
    }
  }

  /**
   * Cancels in-progress PDF generation and returns the UI to the basket list view.
   *
   * @param {HTMLElement} _button - The cancel button (unused; kept for event routing consistency).
   */
  handleBasketCancel(_button: HTMLElement) {
    this.#basket.cancelPdfGeneration();
    this.toggleContainerVisibility('list');
  }

  /**
   * Handles the download action: cancels the PDF generation state, triggers the
   * file download via a temporary anchor, and returns the UI to the basket list view.
   *
   * @param {HTMLElement} _button - The download button (unused; href is read from `#downloadLink`).
   */
  handleDownload(_button: HTMLElement) {
    this.#basket.cancelPdfGeneration();

    if (this.#downloadLink && this.#downloadLink.href) {
      const link = document.createElement('a');
      link.href = this.#downloadLink.href;
      link.download = '';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    this.toggleContainerVisibility('list');
  }

  /**
   * Shows one of the basket's four state panels and hides the rest.
   *
   * Uses the `hidden` CSS class to toggle visibility. The `'error'` state
   * auto-reverts to `'list'` after 5 seconds. Calls that would set the
   * same container that is already visible are short-circuited.
   *
   * @param {'list' | 'progress' | 'success' | 'error'} activeContainer - The panel to show.
   */
  toggleContainerVisibility(activeContainer: 'list' | 'progress' | 'success' | 'error') {
    if (this.#currentVisibleContainer === activeContainer) {
      return;
    }

    const containers = {
      list: this.#basketListContainer,
      progress: this.#basketProgressTemplate,
      success: this.#basketSuccessTemplate,
      error: this.#basketErrorTemplate,
    };

    const targetElement = containers[activeContainer];

    if (targetElement && !targetElement.classList.contains('hidden')) {
      this.#currentVisibleContainer = activeContainer;
      return;
    }

    for (const [key, el] of Object.entries(containers)) {
      if (el && !el.classList.contains('hidden') && key !== activeContainer) {
        el.classList.add('hidden');
      }
    }

    if (targetElement && targetElement.classList.contains('hidden')) {
      targetElement.classList.remove('hidden');

      if (activeContainer === 'error') {
        setTimeout(() => {
          targetElement.classList.add('hidden');
          this.#basketListContainer?.classList.remove('hidden');
          this.#currentVisibleContainer = 'list';
        }, 5000);
      }
    }

    this.#currentVisibleContainer = activeContainer;
  }

  /**
   * Starts PDF generation: shows the progress panel immediately and delegates
   * status updates to `onPdfGenerationStatusUpdate`.
   */
  requestPdfGeneration() {
    this.toggleContainerVisibility('progress');

    this.#basket.requestPdf(this.onPdfGenerationStatusUpdate.bind(this))
      .catch((error) => {
        console.error('[BasketUI] Error requesting PDF generation', error);
        this.toggleContainerVisibility('error');
      });
  }

  /**
   * Called by the poll loop with each job status update.
   *
   * Maps `'new'`/`'running'` → progress panel, `'done'` → success panel (and sets
   * the download link href), `'failed'` → error panel.
   *
   * @param {JobStatus} data - The latest job status from the server.
   */
  onPdfGenerationStatusUpdate(data: JobStatus) {
    if (data?.status === 'running' || data?.status === 'new') {
      this.toggleContainerVisibility('progress');
    } else if (data?.status === 'done') {
      if (this.#downloadLink && data.data?.url) {
        this.#downloadLink.href = data.data.url;
      }
      this.toggleContainerVisibility('success');
    } else if (data?.status === 'failed') {
      this.toggleContainerVisibility('error');
    }
  }

  /**
   * Refreshes all basket-related DOM elements to reflect the current `items` array.
   *
   * Updates: item count badges (and their `aria-hidden` state — hidden when empty),
   * container `data-count` attribute, disabled state of action buttons, the rendered
   * item list (via Mustache), empty-state message visibility, and the hidden state
   * of per-article add/remove buttons.
   */
  updateUI() {
    this.count = this.items.length || 0;

    document.querySelectorAll('.js-basket-count-number').forEach((element) => {
      (element as HTMLElement).textContent = this.count.toString();
    });

    document.querySelectorAll('.basket-count').forEach((element) => {
      (element as HTMLElement).setAttribute('aria-hidden', this.count === 0 ? 'true' : 'false');
    });

    document.querySelectorAll('.js-basket-container').forEach((element) => {
      (element as HTMLElement).dataset.count = this.count.toString();
    });

    document.querySelectorAll('.js-basket-empty').forEach((element) => {
      (element as HTMLButtonElement).disabled = this.items.length === 0;
    });

    document.querySelectorAll('.js-basket-generate').forEach((element) => {
      const generateButton = element as HTMLButtonElement;
      generateButton.disabled = this.count === 0;
      const numberText = generateButton.querySelector('.basket-count-number') as HTMLElement;
      if (numberText) {
        numberText.innerHTML = this.count.toString();
      }
    });

    if (this.#basketListTemplate) {
      const templates: HTMLElement[] = Array.isArray(this.#basketListTemplate)
        ? this.#basketListTemplate
        : [this.#basketListTemplate as HTMLElement];

      templates.forEach((element) => {
        element.innerHTML = '';
        element.hidden = this.items.length === 0;
      });
    }

    if (this.#basketItemTemplate && this.items.length > 0) {
      try {
        this.items.forEach((item) => {
          const renderedItem = Mustache.render(this.#basketItemTemplate!, item);
          this.#basketListTemplate?.insertAdjacentHTML('beforeend', renderedItem);
        });
      } catch (error) {
        console.error('[BasketUI] Error rendering basket items with template:', error);
        this.items.forEach((item) => {
          const listItem = document.createElement('li');
          listItem.className = 'basket-item';
          listItem.innerHTML = `
              <a href="${item.url}" class="basket-item__link">${item.name}</a>
              <button class="js-basket-remove basket-item__remove" data-basket-id="${item.id}">Remove</button>
            `;
          this.#basketListTemplate?.appendChild(listItem);
        });
      }
    }

    document.querySelectorAll('.js-empty-basket-message').forEach((element) => {
      (element as HTMLElement).hidden = this.items.length > 0;
    });

    document.querySelectorAll('.js-basket-add').forEach((button) => {
      const addButton = button as HTMLElement;
      const itemType = addButton.dataset.basketType;
      const itemId = addButton.dataset.basketId;
      addButton.hidden = this.items.some(item => item.type === itemType && item.id === itemId);
    });

    document.querySelectorAll('.js-basket-remove').forEach((button) => {
      const removeButton = button as HTMLElement;
      const itemId = removeButton.dataset.basketId;
      removeButton.hidden = !this.items.some(item => item.id === itemId);
    });
  }

  /**
   * Returns all focusable elements within `container`, excluding hidden ones.
   *
   * @param {HTMLElement} container - The element to search within.
   * @returns {HTMLElement[]} Visible, enabled, focusable elements in DOM order.
   */
  #getFocusableElements(container: HTMLElement): HTMLElement[] {
    const focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    return (Array.from(container.querySelectorAll(focusableSelectors)) as HTMLElement[])
      .filter(el => el.offsetParent !== null && !el.hasAttribute('hidden'));
  }

  /**
   * Installs a `keydown` listener that traps Tab / Shift+Tab focus within `basketElement`
   * and closes the panel on Escape.
   *
   * Removes any previously installed trap before creating a new one.
   *
   * @param {HTMLElement} basketElement - The element whose focusable children form the trap boundary.
   */
  #setupFocusTrap(basketElement: HTMLElement) {
    this.#removeFocusTrap();

    this.#focusTrapHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        const closeButton = basketElement.querySelector('.js-basket-close') as HTMLElement;
        if (closeButton) {
          this.handleBasketClose(closeButton);
        }
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = this.#getFocusableElements(basketElement);
      if (focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', this.#focusTrapHandler);
  }

  /**
   * Removes the active focus trap listener, if any.
   */
  #removeFocusTrap() {
    if (this.#focusTrapHandler) {
      document.removeEventListener('keydown', this.#focusTrapHandler);
      this.#focusTrapHandler = null;
    }
  }

  /**
   * Update status message element with language-specific messages
   * @param {string} action - The action type ('added' | 'removed' | 'error_add' | 'error_remove')
   * @param {string} type - The type of message ('success' | 'error')
   * @param {number} count - The current basket count
   */
  updateStatusMessage(action: 'added' | 'removed' | 'error_add' | 'error_remove', type: 'success' | 'error' = 'success', count: number = this.count) {
    const statusElement = document.getElementById('status-message');
    if (!statusElement) {
      console.warn('[BasketUI] Status message element with id "status-message" not found');
      return;
    }

    const language = this.#basket.settings.language || 'fi';

    const messages: Record<string, { fi: string; en: string }> = {
      added: {
        fi: `Lisätty koriin. Kori sisältää ${count} ${count === 1 ? 'kohteen' : 'kohdetta'}.`,
        en: `Added to basket. Basket contains ${count} ${count === 1 ? 'item' : 'items'}.`
      },
      removed: {
        fi: `Poistettu korista. Kori sisältää ${count} ${count === 1 ? 'kohteen' : 'kohdetta'}.`,
        en: `Removed from basket. Basket contains ${count} ${count === 1 ? 'item' : 'items'}.`
      },
      error_add: {
        fi: 'Virhe lisättäessä koriin.',
        en: 'Error adding to basket.'
      },
      error_remove: {
        fi: 'Virhe poistettaessa korista.',
        en: 'Error removing from basket.'
      }
    };

    const messageSet = messages[action];
    if (!messageSet) {
      console.error('[BasketUI] Unknown action type:', action);
      return;
    }

    const message = language === 'en' ? messageSet.en : messageSet.fi;
    statusElement.textContent = message;
    statusElement.classList.remove('success', 'error');
    statusElement.classList.add(type);
    statusElement.hidden = false;
  }
}


// Singleton instance
const basket = new Basket();
const basketUI = new BasketUI(basket);

export default basketUI;
